<template>
  <div class="container-fluid px-3">
      <h1 class="h2 mb-3">Tin đăng</h1>
      <hr/>

      <!-- Product Info Section -->
      <div class="row mb-3">
        <div class="col-12 col-md-6 col-lg-4 mb-2">
          <b-button v-b-toggle.product-detail variant="primary" class="w-100">
            <b-icon icon="info-circle" class="mr-2"></b-icon>
            Xem thông tin sản phẩm
          </b-button>
        </div>

        <div class="col-12 col-md-6 col-lg-4 mb-2">
          <product-posting-status
            :productid="id"
          >
          </product-posting-status>
        </div>
      </div>

      <!-- Product Detail Collapse -->
      <div class="row">
        <div class="col-12">
          <b-collapse id="product-detail" class="mt-2">
            <b-card>
              <product-detail>
              </product-detail>
            </b-card>
          </b-collapse>
        </div>
      </div>

      <hr/>

      <!-- Posting Editor Section -->
      <div class="row mt-3">
        <!-- Posting Form -->
        <div class="col-12 col-lg-6 mb-4">
          <div class="card">
            <div class="card-header">
              <h2 class="h4 mb-0">Soạn tin đăng</h2>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">Hướng dẫn: tự nhập nội dung đăng tin hoặc sử dụng mẫu tin có sẵn để soạn</p>
              
              <div class="form-group mb-3">
                <label class="form-label fw-bold">Nội dung tin đăng</label>
                <textarea 
                  class="form-control posting-textarea" 
                  v-model="composing"
                  placeholder="Nhập nội dung tin đăng..."
                ></textarea>
              </div>
              
              <div class="form-group mb-3">
                <label class="form-label">Ngày đăng tin</label>
                <b-form-input 
                  type="date" 
                  v-model="form.created_at" 
                  placeholder="Ngày đăng tin"
                  class="form-control"
                />
              </div>

              <div class="form-group mb-3">
                <label class="form-label">Nền tảng đăng tin</label>
                <multiselect 
                  v-model="form.platforms" 
                  :searchable="false"
                  :close-on-select="false"
                  :multiple="true"
                  :options="platformData"
                  label="platform_name"
                  track-by="platform_key"
                  placeholder="Chọn nền tảng đăng tin"
                  class="mobile-multiselect">
                </multiselect>
              </div>

              <div class="form-group mb-3">
                <label class="form-label">Link hình ảnh</label>
                <b-form-input 
                  v-model="content_img_url"
                  placeholder="Nhập link hình ảnh"
                  class="form-control">
                </b-form-input>
              </div>

              <div class="form-group mb-3">
                <label class="form-label">Link sản phẩm</label>
                <b-form-input 
                  v-model="product_url"
                  placeholder="Nhập link sản phẩm"
                  class="form-control">
                </b-form-input>
              </div>

              <!-- Media Upload Section -->
              <div class="form-group mb-3">
                <label class="form-label">Upload hình ảnh đăng tin:</label>
                <div class="upload-section">
                  <!-- Upload Mode Switcher -->
                  <div class="upload-mode-switcher mb-3">
                    <div class="btn-group w-100" role="group">
                      <b-button 
                        :variant="uploadMode === 'local' ? 'primary' : 'outline-primary'"
                        @click="uploadMode = 'local'"
                        class="upload-mode-btn">
                        <b-icon icon="laptop" class="mr-1"></b-icon>
                        Từ máy tính
                      </b-button>
                      <b-button 
                        :variant="uploadMode === 'drive' ? 'primary' : 'outline-primary'"
                        @click="uploadMode = 'drive'"
                        class="upload-mode-btn">
                        <b-icon icon="cloud" class="mr-1"></b-icon>
                        Google Drive
                      </b-button>
                    </div>
                  </div>

                  <!-- Local File Upload Mode -->
                  <div v-if="uploadMode === 'local'" class="upload-method-container mb-3">
                    <div class="upload-button-container">
                      <b-button 
                        variant="primary" 
                        @click="$refs.mediaFileInput.click()"
                        :disabled="uploading"
                        class="w-100"
                        size="lg">
                        <b-icon icon="cloud-upload" class="mr-2"></b-icon>
                        Chọn file từ máy tính ({{ getTotalMediaCount() }})
                      </b-button>
                      <input 
                        ref="mediaFileInput"
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        @change="onMediaFilesSelected"
                        style="display: none">
                      <small class="text-muted d-block mt-2">
                        Hỗ trợ: JPEG, PNG, WebP, MP4 (tối đa 20MB/file)
                      </small>
                    </div>
                  </div>

                  <!-- Google Drive Import Mode -->
                  <div v-if="uploadMode === 'drive'" class="upload-method-container google-drive-import mb-3">
                    <div class="d-flex gap-2 mb-2">
                      <b-form-input 
                        v-model="googleDriveFolderUrl"
                        placeholder="Nhập link thư mục Google Drive..."
                        class="form-control"
                        :disabled="uploading || importingFromDrive">
                      </b-form-input>
                      <b-button 
                        variant="success" 
                        @click="importFromGoogleDrive"
                        :disabled="uploading || importingFromDrive || !googleDriveFolderUrl.trim()"
                        class="flex-shrink-0"
                        size="lg">
                        <b-icon icon="cloud-download" class="mr-1"></b-icon>
                        <span v-if="!importingFromDrive">Import</span>
                        <b-spinner v-else small class="mr-1"></b-spinner>
                        <span v-if="importingFromDrive">Đang import...</span>
                      </b-button>
                    </div>
                    <small class="text-muted d-block">
                      <strong>Hướng dẫn:</strong><br>
                      1. Dán link thư mục Google Drive <strong>đã chia sẻ công khai</strong><br>
                      2. Nhấn nút "Import" để tự động tải hình ảnh<br>
                      <em class="text-info">💡 Thư mục phải được chia sẻ với "Bất kỳ ai có liên kết đều có thể xem"</em>
                    </small>
                  </div>

                  <!-- Upload Progress -->
                  <div v-if="uploading || importingFromDrive" class="upload-progress mb-3">
                    <b-progress :value="uploadProgress" variant="primary" striped animated>
                      <span>{{ uploadProgress }}%</span>
                    </b-progress>
                    <small class="text-muted">
                      {{ importingFromDrive ? 'Đang import từ Google Drive...' : 'Đang tải lên...' }}
                    </small>
                  </div>

                    <!-- Dynamic Media Slots -->
                  <div class="media-slots">
                    <!-- Images Section -->
                    <div v-if="media.images.length > 0 || media.videos.length > 0">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0">Media đã tải lên ({{ getTotalMediaCount() }})</h6>
                        <b-button size="sm" variant="outline-danger" @click="clearAllMedia()" title="Xóa tất cả">
                          <b-icon icon="trash"></b-icon> Xóa tất cả
                        </b-button>
                      </div>
                      
                      <!-- Images -->
                      <div v-if="media.images.length > 0" class="mb-4">
                        <h6 class="text-muted mb-2">Hình ảnh ({{ media.images.length }})</h6>
                        <div class="row">
                          <div class="col-6 col-md-3 col-lg-2 mb-2" v-for="(imageUrl, index) in media.images" :key="'img-' + index">
                            <div class="media-slot has-media">
                              <div class="media-preview">
                                <img :src="imageUrl" :alt="'Hình ' + (index + 1)" class="preview-image">
                                <div class="media-actions">
                                  <b-button size="sm" variant="outline-primary" @click="copyToClipboard(imageUrl)" title="Copy URL">
                                    <b-icon icon="clipboard"></b-icon>
                                  </b-button>
                                  <b-button size="sm" variant="outline-success" @click="insertMediaToContent(imageUrl)" title="Chèn vào nội dung">
                                    <b-icon icon="plus"></b-icon>
                                  </b-button>
                                  <b-button size="sm" variant="outline-danger" @click="removeImage(index)" title="Xóa hình">
                                    <b-icon icon="trash"></b-icon>
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Videos -->
                      <div v-if="media.videos.length > 0">
                        <h6 class="text-muted mb-2">Video ({{ media.videos.length }})</h6>
                        <div class="row">
                          <div class="col-6 col-md-4 col-lg-3 mb-2" v-for="(videoUrl, index) in media.videos" :key="'vid-' + index">
                            <div class="media-slot has-media">
                              <div class="media-preview">
                                <video :src="videoUrl" class="preview-video" controls></video>
                                <div class="media-actions">
                                  <b-button size="sm" variant="outline-primary" @click="copyToClipboard(videoUrl)" title="Copy URL">
                                    <b-icon icon="clipboard"></b-icon>
                                  </b-button>
                                  <b-button size="sm" variant="outline-success" @click="insertMediaToContent(videoUrl)" title="Chèn vào nội dung">
                                    <b-icon icon="plus"></b-icon>
                                  </b-button>
                                  <b-button size="sm" variant="outline-danger" @click="removeVideo(index)" title="Xóa video">
                                    <b-icon icon="trash"></b-icon>
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="empty-media-state">
                      <div class="text-center py-4">
                        <b-icon icon="images" font-scale="3" class="text-muted mb-3"></b-icon>
                        <p class="text-muted mb-0">Chưa có media nào được tải lên</p>
                        <small class="text-muted">Hỗ trợ hình ảnh (JPEG, PNG, WebP) và video (MP4)</small>
                      </div>
                    </div>
                    
                    <small class="text-muted d-block mt-3">
                      Trang sản phẩm sẽ mặc định lấy hình và video từ đây
                    </small>
                  </div>
                </div>
              </div>

              <b-button 
                variant="primary" 
                @click="postNews" 
                v-if="!isLoading"
                class="w-100 btn-lg">
                <b-icon icon="send" class="mr-2"></b-icon>
                Đăng tin
              </b-button>
              <div v-if="isLoading" class="text-center">
                <b-spinner></b-spinner>
                <p class="mt-2">Đang lưu thông tin...</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Template Section -->
        <div class="col-12 col-lg-6 mb-4">
          <div class="card">
            <div class="card-header">
              <b-button v-b-toggle.collapse-1 variant="outline-primary" class="w-100">
                <b-icon icon="file-text" class="mr-2"></b-icon>
                Dùng mẫu tin có sẵn 
                <b-icon icon="arrow-down" class="ml-2"></b-icon>
              </b-button>
            </div>
            <b-collapse id="collapse-1">
              <div class="card-body">
                <div class="form-group mb-3">
                  <label class="form-label">Chọn mẫu tin</label>
                  <b-select 
                    v-model="selectedTemplateContent" 
                    :options="templates" 
                    @change="changeTemplate"
                    class="form-control">
                  </b-select>
                </div>
                
                <div class="form-group mb-3">
                  <label class="form-label">Nội dung mẫu</label>
                  <b-textarea 
                    v-model="selectedTemplateFormatted" 
                    class="form-control template-textarea" 
                    disabled>
                  </b-textarea>
                </div>
                
                <b-button 
                  variant="success" 
                  @click="composing = selectedTemplateFormatted"
                  class="w-100">
                  <b-icon icon="copy" class="mr-2"></b-icon>
                  Copy sang nội dung tin đăng
                </b-button>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>

      <!-- Old Postings Section -->
      <div class="row mt-4">
        <div class="col-12">
          <h2 class="h4 mb-3">Tin đăng cũ</h2>
          
          <!-- Mobile Card View -->
          <div class="d-lg-none">
            <div v-for="(item, index) in postingList" :key="item.id" class="card mb-3">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <small class="text-muted">{{ formatDate(item.created_at) }}</small>
                  <div class="btn-group btn-group-sm">
                    <b-button 
                      size="sm" 
                      variant="outline-primary" 
                      :to="`/public-products/${item.product_id}/posting/${item.id}`"
                      title="Xem trang công khai"
                      target="_blank"
                    >
                      <b-icon icon="eye"></b-icon>
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="remove({item, index})" title="Xóa tin đăng">
                      <b-icon icon="trash"></b-icon>
                    </b-button>
                  </div>
                </div>
                
                <div class="mb-2">
                  <b-form-checkbox
                    v-model="item.is_public"
                    value="1"
                    unchecked-value="0"
                    @change="handleCheckboxChange({item, index, value: item.is_public})"
                  >
                    Hiển thị cho CTV
                  </b-form-checkbox>
                </div>
                
                
                <div class="mb-3">
                  <div class="field-label">Nội dung</div>
                  <div class="field-box preserve-whitespace" v-if="editingIndex !== index">{{ item.content }}</div>
                  <textarea 
                    v-model="item.content" 
                    class="form-control mt-1" 
                    v-if="editingIndex == index">
                  </textarea>
                  <div class="mt-2" v-if="editingIndex == index">
                    <b-button size="sm" variant="primary" @click="savePostingContent({item, index, value: item.content})">
                      Lưu
                    </b-button>
                    <b-button size="sm" variant="secondary" @click="editingIndex = -1">
                      Thoát
                    </b-button>
                  </div>
                  <b-button size="sm" variant="outline-secondary" v-if="editingIndex !== index && item.content" @click="editingIndex = index">
                    Sửa
                  </b-button>
                  <b-button size="sm" variant="outline-primary" v-if="editingIndex !== index && item.content" @click="copyToClipboard(item.content)">
                    Copy
                  </b-button>
                </div>
                
                <div class="mb-3" v-if="item.content_img_url">
                  <div class="field-label">Link hình ảnh</div>
                  <div class="field-box preserve-whitespace" v-if="editingContentUrlIndex !== index">{{ item.content_img_url }}</div>
                  <textarea 
                    v-model="item.content_img_url" 
                    class="form-control mt-1" 
                    v-if="editingContentUrlIndex == index">
                  </textarea>
                  <div class="mt-2" v-if="editingContentUrlIndex == index">
                    <b-button size="sm" variant="primary" @click="savePostingContentImg({item, index, value: item.content_img_url})">
                      Lưu
                    </b-button>
                    <b-button size="sm" variant="secondary" @click="editingContentUrlIndex = -1">
                      Thoát
                    </b-button>
                  </div>
                  <b-button size="sm" variant="outline-secondary" v-if="editingContentUrlIndex !== index" @click="editingContentUrlIndex = index">
                    Sửa
                  </b-button>
                </div>
                
                <div class="mb-3" v-if="item.product_url">
                  <div class="field-label">Link sản phẩm</div>
                  <div class="field-box preserve-whitespace" v-if="editingProductUrlIndex !== index">{{ item.product_url }}</div>
                  <textarea 
                    v-model="item.product_url" 
                    class="form-control mt-1" 
                    v-if="editingProductUrlIndex == index">
                  </textarea>
                  <div class="mt-2" v-if="editingProductUrlIndex == index">
                    <b-button size="sm" variant="primary" @click="savePostingProductUrl({item, index, value: item.product_url})">
                      Lưu
                    </b-button>
                    <b-button size="sm" variant="secondary" @click="editingProductUrlIndex = -1">
                      Thoát
                    </b-button>
                  </div>
                  <b-button size="sm" variant="outline-secondary" v-if="editingProductUrlIndex !== index" @click="editingProductUrlIndex = index">
                    Sửa
                  </b-button>
                </div>

                <!-- Media Display in Mobile Card -->
                <div class="mb-3" v-if="getMediaFromItem(item)">
                  <div class="field-label">Media</div>
                  <div class="mobile-media-grid">
                    <div v-for="(url, key) in getMediaFromItem(item)" :key="key" class="mobile-media-item">
                      <img v-if="key !== 'video'" :src="url" :alt="key" class="mobile-media-thumb" @click="copyToClipboard(url)">
                      <video v-else :src="url" class="mobile-media-thumb" @click="copyToClipboard(url)"></video>
                      <small class="mobile-media-label">{{ key }}</small>
                    </div>
                  </div>
                </div>
                
                
                
                <div class="mb-2">
                  <strong>Nơi đăng:</strong> {{ item.platforms }}
                </div>
                <div class="mb-2">
                  <strong>Đăng bởi:</strong> {{ item.created_by }}
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop Table View -->
          <div class="d-none d-lg-block">
            <b-table 
              :items="postingList" 
              :fields="postingListFields" 
              :busy="isLoading" 
              sticky-header
              head-variant="dark"
              striped outlined bordered small 
              responsive>

              <template v-slot:cell(content)="data">
                <div class="preserve-whitespace" v-if="editingIndex !== data.index">{{ data.value }}</div>
                <textarea 
                  v-model="data.value" 
                  class="form-control" 
                  v-if="editingIndex == data.index">
                </textarea>
                <div class="btn-group btn-group-sm mt-2">
                  <b-button size="sm" v-if="editingIndex !== data.index && data.value != ''" @click="editingIndex = data.index">
                    Sửa
                  </b-button>
                  <b-button size="sm" variant="primary" v-if="editingIndex == data.index" @click="savePostingContent(data)">
                    Lưu
                  </b-button>
                  <b-button size="sm" v-if="editingIndex == data.index" @click="editingIndex = -1">
                    Thoát
                  </b-button>
                  <b-button size="sm" v-if="data.value != ''" @click="copyToClipboard(data.value)">
                    Copy
                  </b-button>
                </div>
              </template>

              <template v-slot:cell(content_img_url)="data">
                <div class="preserve-whitespace" v-if="editingContentUrlIndex !== data.index">{{ data.value }}</div>
                <textarea 
                  v-model="data.value" 
                  class="form-control" 
                  v-if="editingContentUrlIndex == data.index">
                </textarea>
                <div class="btn-group btn-group-sm mt-2">
                  <b-button size="sm" v-if="editingContentUrlIndex !== data.index" @click="editingContentUrlIndex = data.index">
                    Sửa
                  </b-button>
                  <b-button size="sm" variant="primary" v-if="editingContentUrlIndex == data.index" @click="savePostingContentImg(data)">
                    Lưu
                  </b-button>
                  <b-button size="sm" v-if="editingContentUrlIndex == data.index" @click="editingContentUrlIndex = -1">
                    Thoát
                  </b-button>
                </div>
              </template>

              <template v-slot:cell(product_url)="data">
                <div class="preserve-whitespace" v-if="editingProductUrlIndex !== data.index">{{ data.value }}</div>
                <textarea 
                  v-model="data.value" 
                  class="form-control" 
                  v-if="editingProductUrlIndex == data.index">
                </textarea>
                <div class="btn-group btn-group-sm mt-2">
                  <b-button size="sm" v-if="editingProductUrlIndex !== data.index" @click="editingProductUrlIndex = data.index">
                    Sửa
                  </b-button>
                  <b-button size="sm" variant="primary" v-if="editingProductUrlIndex == data.index" @click="savePostingProductUrl(data)">
                    Lưu
                  </b-button>
                  <b-button size="sm" v-if="editingProductUrlIndex == data.index" @click="editingProductUrlIndex = -1">
                    Thoát
                  </b-button>
                </div>
              </template>

              <template v-slot:cell(media)="data">
                <div class="media-table-cell">
                  <div v-if="getMediaFromItem(data.item)" class="media-thumbnails">
                    <div v-for="(url, key) in getMediaFromItem(data.item)" :key="key" class="media-thumb">
                      <img v-if="key !== 'video'" :src="url" :alt="key" class="thumb-image" @click="copyToClipboard(url)">
                      <video v-else :src="url" class="thumb-video" @click="copyToClipboard(url)"></video>
                      <small class="thumb-label">{{ key }}</small>
                    </div>
                  </div>
                  <div v-else class="text-muted">
                    <small>Không có media</small>
                  </div>
                </div>
              </template>

              <template v-slot:cell(is_public)="data">
                <b-form-checkbox
                  v-model="data.value"
                  value="1"
                  unchecked-value="0"
                  @change="handleCheckboxChange(data)"
                >
                  Hiển thị cho CTV
                </b-form-checkbox>
              </template>

              <template v-slot:cell(actions)="data">
                <div class="btn-group btn-group-sm">
                  <b-button 
                    size="sm" 
                    variant="outline-primary" 
                    :to="`/public-products/${data.item.product_id}/posting/${data.item.id}`"
                    title="Xem trang công khai"
                    target="_blank"
                  >
                    <b-icon icon="eye"></b-icon>
                  </b-button>
                  <b-button size="sm" variant="danger" @click="remove(data)" title="Xóa tin đăng">
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                </div>
              </template>
                
              <template v-slot:table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import ProductService from '@/services/productService'
import ProductPostingService from '@/module-product-posting/ProductPostingService'
import ProductPostingTemplate from '@/data/ProductPostingTemplate'
import ProductViewDetail from '@/views/products/ProductViewDetail'
import ProductPostingStatus from '@/module-product-posting/ProductPostingStatus'
import GoogleDriveService from '@/services/googleDriveService'

import Firebase from '@/services/firebase'

import Multiselect from 'vue-multiselect'
import moment from 'moment'

export default {
  name: 'ProductPostingEditor',
  components: {
    Multiselect,
    'product-detail': ProductViewDetail,
    'product-posting-status': ProductPostingStatus // show whether this product is being posted or not
    },
  data () {
    return {
      id: '',
      isLoading: false,
      productInfo: {},
      selectedTemplateContent: '',
      selectedTemplateFormatted: '',
      composing: '',
      content_img_url : '',
      product_url: '',

      // Upload mode selection
      uploadMode: 'local', // 'local' or 'drive'

      // Google Drive import
      googleDriveFolderUrl: '',
      importingFromDrive: false,
      googleDriveService: null,

      // Media upload state - Dynamic arrays for unlimited images
      media: {
        images: [], // Array of image URLs
        videos: []  // Array of video URLs
      },
      uploading: false,
      uploadProgress: 0,

      editingIndex: -1,
      editingContentUrlIndex: -1,
      editingProductUrlIndex: -1,

      templates: ProductPostingTemplate.template.default_data,
      
      platformData: ProductPostingTemplate.platform.default_data,
      form: {
        content_img_url: '',
        product_url: '',
        platforms: [],
        posting_status: 'Đang đăng tin',
        created_by: '',
        created_at: moment().format('yyyy-MM-DD'),
      },

      postingListFields: [
        {
          key:'created_at',
          label: 'Ngày đăng',
          formatter: (value) => {
            return moment(value).format('DD/MM/YYYY')
          }
        }, 
        {
          key:'platforms',
          label: 'Nơi đăng'
        }, 
        'content', 
        'content_img_url',
        'product_url',
        {
          key: 'media',
          label: 'Media'
        },
        {
          key: 'is_public',
          label: 'Hiển thị cho CTV'
        },
        {
          key: 'created_by',
          label: 'Đăng bởi'
        },
        'actions'
      ],
      postingList: []
    }
  },
  watch: {

  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    changeTemplate (evt) {
      console.log(evt);

      var tmp = evt;
      for(var key in this.productInfo) {
        var s = '#' + key + '#';
        if(this.productInfo[key]) {
          tmp = tmp.replace(s, this.productInfo[key]);
        }
      }

      this.selectedTemplateFormatted = tmp;
    },

    async getProductInfo () {
      var productService = new ProductService();
      var productInfo = await productService.getDetail(this.id);
      this.productInfo = productInfo;

      console.log('productInfo ', productInfo);
    },

    async getProductListing () {
      var productPostingService = new ProductPostingService();
      try {
        var tmp = [];

        if(this.form.created_by) {
          // await productPostingService.setGroupConcatMaxLen();
          console.log('ProductPostingEditor::getProductListing::this.$store.getters.role', this.$store.getters.role);
          
          if(this.$store.getters.role != 'admin'){
            tmp = await productPostingService.listByProductIdAndOwner(this.id, this.form.created_by);
          }
          else {
            tmp = await productPostingService.listByProductId(this.id);
          }
        }

        else {
          tmp = await productPostingService.listByProductId(this.id);
        }

        const updatedItems = tmp.map(item => {
          item.platforms = ProductPostingTemplate.generatePlatformNames(item, ProductPostingTemplate.platform.default_data);
          return item;
        });

        this.postingList = updatedItems;

        console.log('ProductPostingEditor::getProductListing ',this.postingList);
      }
      catch(e) {
        alert(e);
      }
    },

    async postNews () {
      this.isLoading = true;

      var tmp = {
        product_id: this.id,
        platform: '',
        content_img_url: this.content_img_url,
        product_url: this.product_url,
        created_at: this.form.created_at,
        created_by: this.form.created_by,
        content: this.composing
      }

      var productPostingService = new ProductPostingService();
      console.log(this.form.platforms);

      // Ensure platforms is an array
      const platforms = Array.isArray(this.form.platforms) ? this.form.platforms : [];

      // Serialize media to JSON - Convert to compatible format
      let mediaForStorage = null;
      if (this.hasMediaContent()) {
        // Create a format compatible with existing data structure and PublicProductPostingViewDetail
        mediaForStorage = {
          images: this.media.images,
          videos: this.media.videos,
          // Legacy format for backward compatibility
          video: this.media.videos.length > 0 ? this.media.videos[0] : null
        };
        
        // Add img1, img2, img3 for backward compatibility
        this.media.images.forEach((url, index) => {
          if (index < 3) {
            mediaForStorage[`img${index + 1}`] = url;
          }
        });
      }
      const mediaJson = mediaForStorage ? JSON.stringify(mediaForStorage) : null;

      try {
        await productPostingService.create_v2(tmp['product_id'], tmp['content'], tmp['content_img_url'], tmp['product_url'], tmp['created_at'], tmp['created_by'], platforms, mediaJson);
      }
      catch(e) {
        alert(e);
      }

      this.getProductListing();

      this.isLoading = false;
    },

    async copyToClipboard (value) {
        try {
          this.composing = value || '';
          if (value) {
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(value);
            } else {
              const textarea = document.createElement('textarea');
              textarea.value = value;
              textarea.setAttribute('readonly', '');
              textarea.style.position = 'absolute';
              textarea.style.left = '-9999px';
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand('copy');
              document.body.removeChild(textarea);
            }
          }
          this.$bvToast && this.$bvToast.toast('Đã copy nội dung tin đăng', { title: 'Copy', variant: 'success', autoHideDelay: 1500 });
        } catch (e) {
          console.error('Copy failed', e);
          alert('Không copy được nội dung. Hãy thử lại.');
        }
    },

    async savePostingContent(dataObj) {
      var productPostingService = new ProductPostingService();
      // call updateProductPostingColumn(id, column, value, type)
      await productPostingService.updateProductPostingColumnById(dataObj.item.id, 'content', dataObj.value, 'text')

      this.postingList[dataObj.index]['content'] = dataObj.value

      this.editingIndex = -1;
    },

    async savePostingContentImg(dataObj) {
      var productPostingService = new ProductPostingService();
      // call updateProductPostingColumn(id, column, value, type)
      await productPostingService.updateProductPostingColumnById(dataObj.item.id, 'content_img_url', dataObj.value, 'text')

      this.postingList[dataObj.index]['content_img_url'] = dataObj.value

      this.editingContentUrlIndex = -1;
    },

    async savePostingProductUrl(dataObj) {
      var productPostingService = new ProductPostingService();
      // call updateProductPostingColumn(id, column, value, type)
      await productPostingService.updateProductPostingColumnById(dataObj.item.id, 'product_url', dataObj.value, 'text')

      this.postingList[dataObj.index]['product_url'] = dataObj.value

      this.editingProductUrlIndex = -1;
    },

    async handleCheckboxChange(dataObj) {
      console.log('ProductPostingEditor::handleCheckboxChange ',dataObj);

      // click on the object. object.item will store the full record
      // {
      //    id: id, 
      // }

      var productPostingService = new ProductPostingService();
      // call updateProductPostingColumn(id, column, value, type)
      await productPostingService.updateProductPostingColumnById(dataObj.item.id, 'is_public', dataObj.value, 'number')

      this.postingList[dataObj.index]['is_public'] = dataObj.value
    },

    async remove(dataObj) {
      this.isLoading = true;

      var r = confirm("Thao tác xoá sẽ không thể phục hồi lại. Bạn có muốn xoá dữ liệu?");
      if (r == true) {
          
      } else {
          return;
      }

      var productPostingService = new ProductPostingService();
      await productPostingService.deleteProductPostingById(dataObj.item.id);
      this.getProductListing();

      this.isLoading = false;
    },

    // Media upload methods
    hasMediaContent() {
      return this.media.images.length > 0 || this.media.videos.length > 0;
    },
    
    getTotalMediaCount() {
      return this.media.images.length + this.media.videos.length;
    },
    
    clearAllMedia() {
      this.media.images = [];
      this.media.videos = [];
      this.$bvToast.toast('Đã xóa tất cả media', { 
        title: 'Media', 
        variant: 'info', 
        autoHideDelay: 1500 
      });
    },

    async onMediaFilesSelected(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      // Validate files
      const validFiles = [];
      for (const file of files) {
        if (this.validateMediaFile(file)) {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) {
        this.$bvToast.toast('Không có file hợp lệ để tải lên', { 
          title: 'Upload', 
          variant: 'warning', 
          autoHideDelay: 2000 
        });
        return;
      }

      await this.uploadMediaFiles(validFiles);
      
      // Clear the input
      event.target.value = '';
    },

    validateMediaFile(file) {
      const maxSize = 20 * 1024 * 1024; // 20MB
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const videoTypes = ['video/mp4'];
      
      if (file.size > maxSize) {
        this.$bvToast.toast(`File "${file.name}" quá lớn (tối đa 20MB)`, { 
          title: 'Upload', 
          variant: 'warning', 
          autoHideDelay: 3000 
        });
        return false;
      }

      if (!imageTypes.includes(file.type) && !videoTypes.includes(file.type)) {
        this.$bvToast.toast(`File "${file.name}" không được hỗ trợ`, { 
          title: 'Upload', 
          variant: 'warning', 
          autoHideDelay: 3000 
        });
        return false;
      }

      return true;
    },

    async uploadMediaFiles(files) {
      this.uploading = true;
      this.uploadProgress = 0;

      try {
        const storage = Firebase.getStorage();
        const timestamp = Date.now();
        let uploadedCount = 0;
        let skippedCount = 0;
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const isVideo = file.type.startsWith('video/');
          
          // Create unique filename
          const extension = file.name.split('.').pop();
          const mediaType = isVideo ? 'video' : 'image';
          const mediaIndex = isVideo ? this.media.videos.length : this.media.images.length;
          const filename = `product_posting/${this.id}/${timestamp}_${mediaType}_${mediaIndex}.${extension}`;
          
          // Upload to Firebase Storage
          const storageRef = storage.ref().child(filename);
          const uploadTask = storageRef.put(file);

          await new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                this.uploadProgress = Math.round((uploadedCount + progress / 100) / files.length * 100);
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              async () => {
                try {
                  const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
                  
                  // Add to appropriate array
                  if (isVideo) {
                    this.media.videos.push(downloadURL);
                  } else {
                    this.media.images.push(downloadURL);
                  }
                  
                  uploadedCount++;
                  resolve();
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        }

        // Show success message
        if (uploadedCount > 0) {
          this.$bvToast.toast(`Đã tải lên thành công ${uploadedCount} file!`, { 
            title: 'Upload', 
            variant: 'success', 
            autoHideDelay: 2000 
          });
        }
        
        if (skippedCount > 0) {
          this.$bvToast.toast(`${skippedCount} file bị bỏ qua do vượt giới hạn`, { 
            title: 'Upload', 
            variant: 'info', 
            autoHideDelay: 2000 
          });
        }

      } catch (error) {
        console.error('Upload failed:', error);
        this.$bvToast.toast('Lỗi khi tải lên: ' + error.message, { 
          title: 'Upload', 
          variant: 'danger', 
          autoHideDelay: 3000 
        });
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },

    removeImage(index) {
      this.media.images.splice(index, 1);
      this.$bvToast.toast('Đã xóa hình ảnh', { 
        title: 'Media', 
        variant: 'info', 
        autoHideDelay: 1500 
      });
    },
    
    removeVideo(index) {
      this.media.videos.splice(index, 1);
      this.$bvToast.toast('Đã xóa video', { 
        title: 'Media', 
        variant: 'info', 
        autoHideDelay: 1500 
      });
    },

    insertMediaToContent(url) {
      if (url && this.composing !== null && this.composing !== undefined) {
        this.composing += '\n' + url;
        this.$bvToast.toast('Đã chèn link vào nội dung', { 
          title: 'Media', 
          variant: 'success', 
          autoHideDelay: 1500 
        });
      }
    },

    // Google Drive import methods
    async importFromGoogleDrive() {
      if (!this.googleDriveFolderUrl.trim()) {
        this.$bvToast.toast('Vui lòng nhập link thư mục Google Drive', { 
          title: 'Import', 
          variant: 'warning', 
          autoHideDelay: 2000 
        });
        return;
      }

      this.importingFromDrive = true;
      this.uploadProgress = 0;

      try {
        // Initialize Google Drive service if not already done
        if (!this.googleDriveService) {
          this.googleDriveService = new GoogleDriveService();
        }

        this.$bvToast.toast('Bắt đầu import từ Google Drive...', { 
          title: 'Import', 
          variant: 'info', 
          autoHideDelay: 2000 
        });

        // Import images from Google Drive folder with progress callback
        const result = await this.googleDriveService.importImagesFromFolder(
          this.googleDriveFolderUrl,
          (progress) => {
            // Update progress during download phase (0-50%)
            this.uploadProgress = Math.round((progress.current / progress.total) * 50);
            console.log(`Downloading ${progress.fileName} (${progress.current}/${progress.total})`);
          }
        );
        
        this.uploadProgress = 50; // Download phase complete

        if (result.files.length === 0) {
          if (result.errors.length > 0) {
            this.$bvToast.toast(`Không thể tải xuống file nào. Lỗi: ${result.errors[0]}`, { 
              title: 'Import', 
              variant: 'warning', 
              autoHideDelay: 5000 
            });
          } else {
            this.$bvToast.toast('Không tìm thấy file hình ảnh nào trong thư mục', { 
              title: 'Import', 
              variant: 'warning', 
              autoHideDelay: 3000 
            });
          }
          return;
        }

        console.log(`Starting Firebase upload for ${result.files.length} files...`);
        
        // Upload imported files to Firebase Storage
        await this.uploadMediaFiles(result.files);

        // Show success message with details
        let message = `Đã import thành công ${result.successCount}/${result.totalFound} file!`;
        if (result.errors.length > 0) {
          message += ` (${result.errors.length} file lỗi)`;
          console.warn('Import errors:', result.errors);
          
          // Show detailed error information
          this.$bvToast.toast(`Chi tiết lỗi:\n${result.errors.slice(0, 3).join('\n')}`, { 
            title: 'Import Errors', 
            variant: 'warning', 
            autoHideDelay: 5000 
          });
        }

        this.$bvToast.toast(message, { 
          title: 'Import', 
          variant: 'success', 
          autoHideDelay: 3000 
        });

        // Clear the input after successful import
        this.googleDriveFolderUrl = '';

      } catch (error) {
        console.error('Google Drive import failed:', error);
        
        let errorMessage = 'Lỗi khi import từ Google Drive';
        if (error.message) {
          errorMessage += ': ' + error.message;
        }
        
        // Provide more helpful error messages
        if (error.message.includes('403') || error.message.includes('Access denied')) {
          errorMessage += '\n\nVui lòng đảm bảo:\n1. Thư mục được chia sẻ công khai\n2. Google Drive API đã được bật';
        }
        
        this.$bvToast.toast(errorMessage, { 
          title: 'Import Error', 
          variant: 'danger', 
          autoHideDelay: 8000 
        });
      } finally {
        this.importingFromDrive = false;
        this.uploadProgress = 0;
      }
    },

    getMediaFromItem(item) {
      if (!item || !item.media) return null;
      
      try {
        const media = JSON.parse(item.media);
        const validMedia = {};
        
        // Handle new format with arrays
        if (media.images && Array.isArray(media.images)) {
          media.images.forEach((url, index) => {
            if (url && url.trim() !== '') {
              validMedia[`img${index + 1}`] = url;
            }
          });
        }
        
        if (media.videos && Array.isArray(media.videos)) {
          media.videos.forEach((url, index) => {
            if (url && url.trim() !== '') {
              validMedia[index === 0 ? 'video' : `video${index + 1}`] = url;
            }
          });
        }
        
        // Handle legacy format (img1, img2, img3, video)
        Object.keys(media).forEach(key => {
          if (!['images', 'videos'].includes(key) && media[key] && media[key].trim() !== '') {
            validMedia[key] = media[key];
          }
        });
        
        return Object.keys(validMedia).length > 0 ? validMedia : null;
      } catch (error) {
        console.error('Error parsing media JSON:', error);
        return null;
      }
    }
  },
  mounted () {

    this.id = this.$route.params.id;
    this.getProductInfo();
    this.getProductListing();
    this.form.created_by = this.$store.getters.userEmail;

    // Ensure platforms is properly initialized as an array
    if (!Array.isArray(this.form.platforms)) {
      this.form.platforms = [];
    }
    
    // Ensure media arrays are properly initialized
    if (!Array.isArray(this.media.images)) {
      this.media.images = [];
    }
    if (!Array.isArray(this.media.videos)) {
      this.media.videos = [];
    }

    // Initialize Google Drive service
    this.googleDriveService = new GoogleDriveService();

    console.log('ProductPostingEditor::mounted::this.form.created_by', this.form.created_by);
    
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* Mobile-first responsive design */
.container-fluid {
  max-width: 1200px;
  margin: 0 auto;
  background-color: #ffffff;
  min-height: 100vh;
  padding: 20px 15px;
}

/* Responsive textarea heights */
.posting-textarea {
  min-height: 200px;
  resize: vertical;
}

.template-textarea {
  min-height: 150px;
  resize: vertical;
}

@media (min-width: 768px) {
  .posting-textarea {
    min-height: 300px;
  }
  
  .template-textarea {
    min-height: 200px;
  }
}

@media (min-width: 992px) {
  .posting-textarea {
    min-height: 400px;
  }
  
  .template-textarea {
    min-height: 250px;
  }
}

/* Mobile multiselect improvements */
.mobile-multiselect {
  min-height: 44px; /* Touch-friendly minimum height */
}

/* Preserve whitespace for content display */
.preserve-whitespace {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
  overflow-wrap: break-word;
}

  /* Bordered field box for readability on mobile */
  .field-box {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 10px 12px;
    background-color: #fff;
    margin-top: 6px;
  }

  .field-label {
    font-weight: 600;
    color: #495057;
    font-size: 0.95rem;
  }

/* Card improvements for mobile */
.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

/* Button improvements for mobile */
.btn {
  border-radius: 6px;
  font-weight: 500;
  border-width: 2px;
}

.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
}

/* Table button improvements for better readability */
.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  border-width: 2px;
}

/* Ensure table buttons have proper contrast */
.table .btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: #ffffff;
}

.table .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #ffffff;
}

.table .btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #ffffff;
}

.table .btn-outline-primary {
  background-color: #ffffff;
  border-color: #0d6efd;
  color: #0d6efd;
}

.table .btn-outline-secondary {
  background-color: #ffffff;
  border-color: #6c757d;
  color: #6c757d;
}

.table .btn-outline-danger {
  background-color: #ffffff;
  border-color: #dc3545;
  color: #dc3545;
}

/* Button hover states for better interaction */
.btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.table .btn-outline-primary:hover {
  background-color: #0d6efd;
  color: #ffffff;
}

.table .btn-outline-secondary:hover {
  background-color: #6c757d;
  color: #ffffff;
}

.table .btn-outline-danger:hover {
  background-color: #dc3545;
  color: #ffffff;
}

/* Form improvements */
.form-control {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 10px 12px;
  font-size: 16px; /* Prevent zoom on iOS */
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-label {
  font-weight: 600;
  margin-bottom: 8px;
  color: #495057;
}

/* Table improvements for mobile */
@media (max-width: 991.98px) {
  .table-responsive {
    border: none;
  }
  
  .table td, .table th {
    padding: 8px 4px;
    font-size: 14px;
  }
}

/* Button group improvements */
.btn-group-sm > .btn {
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  border-width: 2px;
  margin: 0 2px;
}

/* Table button group specific styling */
.table .btn-group-sm > .btn {
  min-width: 60px;
  text-align: center;
  border-radius: 4px;
}

/* Ensure button text is always readable */
.table .btn-group .btn {
  text-shadow: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* Mobile button improvements */
@media (max-width: 767.98px) {
  .table .btn-group-sm > .btn {
    padding: 8px 12px;
    font-size: 14px;
    min-width: 70px;
  }
}

/* Loading spinner improvements */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* Multiselect custom styles for mobile */
:deep(.multiselect) {
  min-height: 44px;
}

:deep(.multiselect__tags) {
  min-height: 44px;
  padding: 8px 12px;
}

:deep(.multiselect__input) {
  font-size: 16px;
}

:deep(.multiselect__option) {
  padding: 12px 16px;
  min-height: 44px;
}

/* Responsive spacing */
@media (max-width: 767.98px) {
  .container-fluid {
    padding-left: 15px;
    padding-right: 15px;
  }
  
  .card-body {
    padding: 15px;
  }
  
  .btn-group {
    flex-wrap: wrap;
  }
  
  .btn-group > .btn {
    margin-bottom: 4px;
  }
}

/* Touch-friendly improvements */
@media (max-width: 767.98px) {
  .btn {
    min-height: 44px;
    padding: 12px 16px;
  }
  
  .form-control {
    min-height: 44px;
  }
  
  .preserve-whitespace {
    font-size: 14px;
    line-height: 1.5;
  }
}

/* Ensure good contrast and readability */
.card {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.form-control {
  background-color: #ffffff;
  border: 1px solid #ced4da;
  color: #212529;
}

.form-control:focus {
  background-color: #ffffff;
  border-color: #80bdff;
  color: #212529;
}

.form-label {
  color: #212529;
  font-weight: 600;
}

/* Ensure text is readable in all contexts */
.preserve-whitespace {
  color: #212529;
}

.text-muted {
  color: #6c757d !important;
}

/* Button text contrast */
.btn {
  color: #ffffff;
}

.btn-outline-primary {
  color: #0d6efd;
  border-color: #0d6efd;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
}

/* Table improvements for better readability */
.table {
  background-color: #ffffff;
}

.table th {
  background-color: #f8f9fa;
  color: #212529;
}

.table td {
  color: #212529;
}

/* Additional text contrast improvements */
h1, h2, h3, h4, h5, h6 {
  color: #212529;
}

p, span, div {
  color: #212529;
}

/* Multiselect improvements for better contrast */
:deep(.multiselect) {
  background-color: #ffffff;
}

:deep(.multiselect__tags) {
  background-color: #ffffff;
  color: #212529;
}

:deep(.multiselect__input) {
  color: #212529;
}

:deep(.multiselect__placeholder) {
  color: #6c757d;
}

:deep(.multiselect__option) {
  background-color: #ffffff;
  color: #212529;
}

:deep(.multiselect__option--highlight) {
  background-color: #e9ecef;
  color: #212529;
}

/* Ensure all form elements have proper contrast */
.form-select {
  background-color: #ffffff;
  color: #212529;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.form-check-label {
  color: #212529;
}

/* Media Upload Styles */
.upload-section {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
}

/* Upload Mode Switcher Styles */
.upload-mode-switcher {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid #dee2e6;
}

.upload-mode-switcher .btn-group {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  overflow: hidden;
}

.upload-mode-btn {
  font-weight: 600;
  padding: 12px 16px;
  transition: all 0.3s ease;
  border-radius: 0 !important;
}

.upload-mode-btn:first-child {
  border-top-left-radius: 6px !important;
  border-bottom-left-radius: 6px !important;
}

.upload-mode-btn:last-child {
  border-top-right-radius: 6px !important;
  border-bottom-right-radius: 6px !important;
}

.upload-method-container {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Google Drive Import Styles */
.google-drive-import {
  border: 1px solid #d4edda;
  border-radius: 6px;
  padding: 15px;
  background-color: #f1f9f3;
}

.google-drive-import .d-flex {
  gap: 8px;
}

@media (max-width: 576px) {
  .google-drive-import .d-flex {
    flex-direction: column;
    gap: 8px;
  }
  
  .google-drive-import .flex-shrink-0 {
    flex-shrink: 1;
  }
  
  .upload-mode-btn {
    padding: 10px 12px;
    font-size: 14px;
  }
}

.empty-media-state {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background-color: #ffffff;
}

.upload-progress {
  margin: 10px 0;
}

.media-slots .row {
  margin: 0 -5px;
}

.media-slots .col-6,
.media-slots .col-md-3 {
  padding: 0 5px;
}

.media-slot {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: #ffffff;
  transition: all 0.2s ease;
  min-height: 120px;
}

.media-slot.has-media {
  border-color: #28a745;
  border-style: solid;
}

.media-placeholder {
  text-align: center;
  color: #6c757d;
  padding: 10px;
}

.media-preview {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.preview-image,
.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.media-actions {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.media-preview:hover .media-actions {
  opacity: 1;
}

.media-actions .btn {
  padding: 4px 6px;
  font-size: 12px;
  line-height: 1;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.media-actions .btn:hover {
  background-color: #ffffff;
  transform: none;
}

/* Responsive adjustments for media slots */
@media (max-width: 576px) {
  .media-slot {
    aspect-ratio: 1.2;
  }
  
  .media-actions {
    position: static;
    opacity: 1;
    flex-direction: row;
    justify-content: center;
    margin-top: 5px;
    background-color: rgba(248, 249, 250, 0.9);
    padding: 5px;
    border-radius: 4px;
  }
  
  .upload-section {
    padding: 10px;
  }
}

/* Media Table and Mobile Display Styles */
.media-table-cell {
  max-width: 200px;
}

.media-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.media-thumb {
  text-align: center;
  cursor: pointer;
}

.thumb-image,
.thumb-video {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  transition: transform 0.2s ease;
}

.thumb-image:hover,
.thumb-video:hover {
  transform: scale(1.1);
}

.thumb-label {
  display: block;
  font-size: 10px;
  color: #6c757d;
  margin-top: 2px;
}

.mobile-media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}

.mobile-media-item {
  text-align: center;
  cursor: pointer;
}

.mobile-media-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  transition: transform 0.2s ease;
}

.mobile-media-thumb:hover {
  transform: scale(1.05);
}

.mobile-media-label {
  display: block;
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
}
</style>
